import React from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Lock,
  Clock3,
  AlertTriangle,
  CircleAlert,
  CheckCircle2,
  Wrench,
} from "lucide-react";

/**
 * WidgetCard — Phase C Wave 1 universal widget renderer.
 *
 * Handles every widget key with a single layout:
 *   [icon]  Label
 *           BIG NUMBER (or status text)
 *           Subtitle / label
 *   (chevron click-through to detail page)
 *
 * Variants (driven by props / server payload):
 *   status = "normal" | "watch" | "attention"       → color accent
 *   is_placeholder = true                            → muted, "Coming soon"
 *   unavailable_reason = "permission_denied" | ...   → muted, restricted / n/a
 *
 * Does NOT know anything about specific widget business logic — the
 * server-side aggregator (/api/dashboard/summary) does all math and
 * hands back the payload. This is the ONLY dashboard renderer shipped
 * in Wave 1 — no charts, lists, maps, or per-widget custom UI.
 */

const STATUS_TOKENS = {
  normal:    { accent: "text-carbon dark:text-road-white",       bar: "bg-coach-blue" },
  watch:     { accent: "text-amber-hold",                         bar: "bg-amber-hold" },
  attention: { accent: "text-critical-red",                       bar: "bg-critical-red" },
};

// Best-effort click-through mapping. Keys not listed here render as a
// non-linked card. Wave 2A: no new routes are invented — deep-linked
// query params are only used when the target page already supports them.
// ToursList `/tours` supports a `status` query hint (best-effort filter);
// other pages accept the click-through as a plain navigation.
const WIDGET_ROUTES = {
  // Operations
  active_tours:            "/tours?status=active_tour",
  upcoming_tours:          "/schedule",
  upcoming_departures:     "/schedule",
  departure_readiness:     "/dispatch",
  operations_watch:        "/dispatch",
  // Fleet
  fleet_availability:      "/fleet",
  coaches_touring:         "/fleet",
  coaches_available:       "/fleet",
  coaches_in_service:      "/fleet",
  current_coach_locations: "/fleet",
  // Drivers
  driver_availability:     "/drivers",
  drivers_assigned:        "/drivers",
  drivers_needing_assignment: "/dispatch",
  driver_hos_attention:    "/drivers",
  // Leasing / Commercial
  holds:                   "/tours",
  first_holds:             "/tours?status=first_hold",
  second_holds:            "/tours?status=second_hold",
  upcoming_coach_needs:    "/tours",
  pricing_requiring_attention: "/quotes",
  quote_pipeline:          "/reports/quote-pipeline",
  quotes_awaiting_customer:"/reports/quote-pipeline",
  contracts_pending:       "/quotes",
  // Routing / Documents
  routing_awaiting_review: "/schedule",
  pickup_sheets:           "/documents",
  departure_documents:     "/documents",
  // Financial
  billing_status:          "/billing",
  outstanding_payments:    "/billing",
  upcoming_payments:       "/billing",
  revenue_snapshot:        "/reports",
  internal_cost_margin_indicators: "/reports",
  // Future Shop
  open_shop_work:          "/service",
  coaches_in_shop:         "/service",
  technician_workload:     "/service",
  parts_needed:            "/service",
  work_waiting_on_parts:   "/service",
  interior_build_projects: "/service",
  cleaning_required:       "/service",
  detailing_required:      "/service",
  shop_completion_schedule:"/service",
};

const REASON_LABELS = {
  permission_denied: "Restricted",
  future_shop: "Future Shop Module",
  motive_telemetry_placeholder: "Awaiting live telemetry",
  motive_hos_placeholder: "Awaiting HOS feed",
  no_checklist_endpoint: "Data source pending",
  no_routing_review_endpoint: "Data source pending",
  no_departure_docs_endpoint: "Data source pending",
  no_billing_endpoint: "Billing module pending",
  computation_error: "Temporarily unavailable",
  unknown_widget: "Unknown widget",
};

/**
 * Format the primary value + subtitle from arbitrary server payload.
 * Server contract (Wave 1): `data` may contain either `count`, or `count`
 * plus `of` (fraction), plus a human `label`. Anything richer is a
 * Wave 2 renderer.
 */
const renderValue = (data) => {
  if (!data || typeof data !== "object") return { primary: "—", subtitle: null };
  const { count, of, label } = data;
  let primary = "—";
  if (typeof count === "number") {
    primary = typeof of === "number" ? `${count} / ${of}` : String(count);
  }
  return { primary, subtitle: label || null };
};

export function WidgetCard({
  widgetKey,
  label,
  category,
  payload,
  isFuturePlaceholder = false,
}) {
  const status = payload?.status || "normal";
  const runtimePh = payload?.is_placeholder === true;
  const reason = payload?.unavailable_reason || null;

  // Phase C Wave 2A.1 — tone priority:
  //   1. Future Shop / planned capability (catalog is_future_placeholder)
  //      always wins over permission_denied. A widget that represents an
  //      unbuilt module is not "restricted" — it's simply not shipped yet.
  //   2. permission_denied (runtime perm failure on a live widget) → Restricted.
  //   3. Runtime is_placeholder (data source not yet wired) → placeholder.
  //   4. Otherwise → live.
  if (isFuturePlaceholder) {
    return (
      <MutedShell testid={`widget-${widgetKey}`} tone="future">
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
          <Wrench size={11} /> {label}
        </div>
        <div className="mt-2 text-[13px] font-medium text-steel dark:text-coach-silver"
             data-testid={`widget-${widgetKey}-future`}>
          {REASON_LABELS.future_shop}
        </div>
        <div className="mt-1 text-[11px] text-steel">
          Planned capability · {category}
        </div>
      </MutedShell>
    );
  }

  const denied = reason === "permission_denied";
  if (denied) {
    return (
      <MutedShell testid={`widget-${widgetKey}`} tone="denied">
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
          <Lock size={11} /> {label}
        </div>
        <div className="mt-2 text-[13px] font-medium text-steel dark:text-coach-silver" data-testid={`widget-${widgetKey}-restricted`}>
          Restricted
        </div>
        <div className="mt-1 text-[11px] text-steel">
          You don't have permission to view this widget.
        </div>
      </MutedShell>
    );
  }

  if (runtimePh) {
    return (
      <MutedShell testid={`widget-${widgetKey}`} tone="placeholder">
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
          <Clock3 size={11} /> {label}
        </div>
        <div className="mt-2 text-[13px] font-medium text-steel dark:text-coach-silver" data-testid={`widget-${widgetKey}-placeholder`}>
          {REASON_LABELS[reason] || "Coming soon"}
        </div>
        <div className="mt-1 text-[11px] text-steel">
          {category} · widget renderer
        </div>
      </MutedShell>
    );
  }

  // ---- Live variant ----
  const to = WIDGET_ROUTES[widgetKey] || null;
  const { primary, subtitle } = renderValue(payload?.data);
  const tokens = STATUS_TOKENS[status] || STATUS_TOKENS.normal;
  const StatusIcon =
    status === "attention" ? CircleAlert :
    status === "watch"     ? AlertTriangle :
                             CheckCircle2;

  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-widest text-steel truncate">{label}</div>
        <StatusIcon
          size={12}
          className={
            status === "attention" ? "text-critical-red" :
            status === "watch"     ? "text-amber-hold" :
                                     "text-touring-lime"
          }
        />
      </div>
      <div
        className={`font-display leading-tight mt-2 text-[30px] tabular-nums ${tokens.accent}`}
        data-testid={`widget-${widgetKey}-value`}
      >
        {primary}
      </div>
      {subtitle && (
        <div className="text-[11.5px] text-steel mt-1 truncate" title={subtitle}>{subtitle}</div>
      )}
      <div className={`mt-3 h-[2px] w-8 ${tokens.bar}`} />
    </>
  );

  const base = "block bg-white dark:bg-graphite border border-coach-silver dark:border-graphite rounded-md p-4 min-h-[124px] flex flex-col";
  if (to) {
    return (
      <Link
        to={to}
        data-testid={`widget-${widgetKey}`}
        className={`${base} group transition hover:border-coach-blue`}
      >
        <div className="flex-1">{inner}</div>
        <div className="mt-2 flex justify-end">
          <ChevronRight size={14} className="text-steel group-hover:text-coach-blue transition" />
        </div>
      </Link>
    );
  }
  return (
    <div data-testid={`widget-${widgetKey}`} className={base}>
      {inner}
    </div>
  );
}

const MutedShell = ({ children, testid, tone }) => (
  <div
    data-testid={testid}
    data-widget-tone={tone}
    className="bg-road-white/60 dark:bg-carbon/40 border border-dashed border-coach-silver dark:border-steel/40 rounded-md p-4 min-h-[124px] flex flex-col"
  >
    {children}
  </div>
);

export default WidgetCard;
